import { z } from "zod/v4";

export const LiteralSchema = z.union([z.string(), z.number(), z.boolean()]);

export type LiteralType = z.infer<typeof LiteralSchema>;

export const JsonSchema: z.ZodType<JsonType> = z.lazy(() =>
  z.union([
    LiteralSchema,
    z.array(JsonSchema),
    z.record(z.union([z.string(), z.number(), z.symbol()]), JsonSchema),
  ])
);

export const castToNumber = z.preprocess(
  (val: unknown) => (typeof val === "string" ? Number(val) : val),
  z.number().or(z.string())
);

export const castToString = z.preprocess(
  (val: unknown) => (typeof val === "number" ? val.toString() : val),
  z.string()
);

export const castToBool = z.preprocess(
  (val: unknown) => (typeof val === "string" ? stringToBoolean(val) : val),
  z.boolean()
);

// Like castToBool, but never throws: an unrecognized or malformed value degrades
// to `false` (and a missing/null value to `undefined`) instead of failing the
// whole parse. Use for booleans authored by hand - e.g. Stripe dashboard
// metadata - where a typo or stray whitespace must not break parsing of the
// surrounding object.
export const castToBoolLenient = z.preprocess((val: unknown) => {
  if (typeof val === "boolean") {
    return val;
  }
  if (typeof val === "string") {
    return ["y", "yes", "t", "true", "on", "1"].includes(
      val.trim().toLowerCase()
    );
  }
  return undefined;
}, z.boolean().optional());

export const castToEpochTimestamp = z
  .preprocess(
    (val) => (typeof val === "number" ? String(val) : val),
    z.string()
  )
  .refine((val) => /^\d{10}$/.test(val), {
    error: "Must be a 10-digit epoch timestamp",
  });

export const castToNumberOrBoolean = z.preprocess((value: any) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (!isNaN(Number(value))) {
    // Coerce to number if possible
    return Number(value);
  } else if (typeof value === "string") {
    return stringToBoolean(value);
  }

  // Unknown shape — return as-is and let z.boolean().or(z.number()) reject it
  // with a ZodError, which the handler middleware converts to HTTP 400.
  return value;
}, z.boolean().or(z.number()));

export const castToCommaList = z.preprocess(
  (arg: unknown) => (Array.isArray(arg) ? arg.join(",") : arg),
  z.string()
);

export const commaListToArray = z.preprocess((arg: unknown) => {
  if (arg === null || arg === undefined) {
    return [];
  }
  return Array.isArray(arg)
    ? arg
    : typeof arg === "string"
      ? arg.split(",")
      : arg;
}, z.string().array());

export type JsonType = LiteralType | { [key: string]: JsonType } | JsonType[];

const stringToBoolean = (value: string): boolean => {
  const v = value.toLowerCase();
  if (["y", "yes", "t", "true", "on", "1"].includes(v)) {
    return true;
  }
  if (["n", "no", "f", "false", "off", "0"].includes(v)) {
    return false;
  }
  throw new z.ZodError([
    { code: "custom", message: `Invalid boolean value: ${v}`, path: [] },
  ]);
};

export const validateIdNotExponent = z
  .string()
  .refine((val) => !val.toUpperCase().includes("E"), {
    error: "ID cannot be in exponential form",
  });

// Extract union of discriminator literal values from a discriminated union
type ExtractDiscriminatorUnion<T> =
  T extends z.ZodDiscriminatedUnion<infer Opts, infer Disc extends string>
    ? Opts extends readonly any[]
      ? Opts[number] extends { shape: infer S }
        ? S extends Record<Disc, infer V>
          ? V extends z.ZodLiteral<infer L>
            ? L
            : never
          : never
        : never
      : never
    : never;

/**
 * Extracts a z.enum from a z.discriminatedUnion based on the discriminator field values.
 *
 * @example
 * const MyUnion = z.discriminatedUnion('type', [
 *   z.object({ type: z.literal('foo'), data: z.string() }),
 *   z.object({ type: z.literal('bar'), data: z.number() }),
 * ]);
 * const MyEnum = extractDiscriminatorEnum(MyUnion);
 * // MyEnum is z.enum(['foo', 'bar'])
 * // z.infer<typeof MyEnum> is 'foo' | 'bar'
 */
export function extractDiscriminatorEnum<
  T extends z.ZodDiscriminatedUnion<any, any>,
>(
  discriminatedUnion: T
): z.ZodEnum<
  Record<ExtractDiscriminatorUnion<T>, ExtractDiscriminatorUnion<T>>
> {
  const discriminatorKey = discriminatedUnion.def.discriminator;
  const values = discriminatedUnion.options.map(
    (option) => option.shape[discriminatorKey].value
  );
  return z.enum(values);
}

export type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? RecursivePartial<U>[]
    : T[P] extends object
      ? RecursivePartial<T[P]>
      : T[P];
};

export type RecursiveRequired<T> = {
  [K in keyof T]: RecursiveRequired<T[K]>;
} & Required<T>;

export type PathsToProps<T> = T extends string | number | boolean
  ? []
  : {
      [K in Extract<keyof T, string | number | boolean>]: [
        K,
        ...PathsToProps<T[K]>,
      ];
    }[Extract<keyof T, string | number | boolean>];

export type Join<T extends string[], D extends string> = T extends []
  ? never
  : T extends [infer F]
    ? F
    : T extends [infer F, ...infer R]
      ? F extends string
        ? `${F}${D}${Join<Extract<R, string[]>, D>}`
        : never
      : string;

// @ts-ignore
export type NestedKey<T> = Join<PathsToProps<T>, ".">;

export type NestedValue<T, K extends string> = K extends keyof T
  ? T[K]
  : K extends `${infer K0}.${infer KR}`
    ? K0 extends keyof T
      ? NestedValue<T[K0], KR>
      : never
    : never;

export type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

export type WithRequired<T, K extends keyof T> = Omit<T, K> & {
  [P in K]-?: NonNullable<T[P]>;
};

export type Nullable<T> = { [K in keyof T]: T[K] | null };

export type ExplicitUndefined<T> = {
  [K in keyof T]: T[K] | undefined;
};

export type CamelToSnakeCase<S extends string> =
  S extends `${infer T}${infer U}`
    ? T extends Capitalize<T>
      ? `_${Lowercase<T>}${CamelToSnakeCase<U>}`
      : `${T}${CamelToSnakeCase<U>}`
    : S;

export type ConvertKeysToSnakeCase<T> = {
  [K in keyof T as CamelToSnakeCase<string & K>]: T[K] extends Record<
    string,
    any
  >
    ? ConvertKeysToSnakeCase<T[K]>
    : T[K];
};

export type Flatten<T> = {
  [K in keyof T]: T[K] extends object ? Flatten<T[K]> : T[K];
}[keyof T];

export type PickAction<T, K> = T extends { action: K } ? T : never;
export type PickType<T, K> = T extends { type: K } ? T : never;

export type PickTypeName<T, K> = T extends { __typename?: K }
  ? T
  : T extends null
    ? never
    : never;

export type FirstParameter<T extends (...args: any[]) => any> =
  Parameters<T>[0];

// Type computation utilities to avoid deep instantiation errors

/**
 * Helper to safely cast types and avoid deep instantiation
 */
export type Cast<T, U> = T extends U ? T : U;

/**
 * Safely omit properties while avoiding deep instantiation errors
 */
export type SafeOmit<T, K extends keyof any> =
  Omit<T, K> extends infer U ? Cast<U, Record<string, unknown>> : never;

/**
 * Safely pick properties while avoiding deep instantiation errors
 */
export type SafePick<T, K extends keyof T> =
  Pick<T, K> extends infer U ? Cast<U, Record<string, unknown>> : never;

/**
 * Safely extend a type with additional properties
 */
export type SafeExtend<T, U> = T extends infer Base
  ? Cast<Base, Record<string, unknown>> & U
  : never;

/**
 * Break up any complex type computation
 */
export type BreakComputation<T> = T extends infer U ? Cast<U, unknown> : never;

/**
 * Safely handle array types to avoid deep instantiation
 */
export type SafeArray<T> = T[] extends infer U ? Cast<U, unknown[]> : never;

export interface QuantityButtonsSelectedVariant {
  id: string;
  quantity: number;
  // Live Shopify prices carried over from QuantityButtonsVariant;
  // compareAtPrice is only meaningful when price is present (live fetch
  // succeeded).
  price?: string | number;
  compareAtPrice?: string | number;
}
